import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * DarkTooltip — tooltip estilizado padrão DarkPlanner.
 *
 * Substitui o tooltip nativo (`title=""`) por um balão React renderizado
 * em portal no document.body. Cores adaptam ao tema via CSS vars.
 *
 * Estratégia de posicionamento (two-pass measurement):
 *   1. Ao abrir, renderiza o tooltip em (-9999, -9999) com opacity:0.
 *   2. useLayoutEffect mede AMBOS os retângulos reais (trigger + tooltip)
 *      ANTES do paint via getBoundingClientRect().
 *   3. computePosition decide o lado real (flip automático top↔bottom /
 *      left↔right se o lado preferido não couber) usando a altura REAL
 *      do tooltip (não estimativa).
 *   4. setCoords({ ..., ready:true }) dispara re-render e a transition
 *      de opacity faz fade-in suave (12ms).
 *   5. scroll/resize recalcula posição em vez de fechar — tooltip segue
 *      o anchor.
 *
 * Uso:
 *   <DarkTooltip content="Texto simples">
 *     <button>Hover me</button>
 *   </DarkTooltip>
 *
 *   <DarkTooltip content={<div>Rich JSX</div>} delay={600} maxWidth={300}>
 *     <div>...</div>
 *   </DarkTooltip>
 *
 * Props:
 *   - content: string | ReactNode — texto ou JSX do tooltip
 *   - position: 'top' | 'bottom' | 'left' | 'right' (default 'top')
 *   - delay: ms antes de mostrar (default 400)
 *   - maxWidth: px (default 240)
 *   - children: 1 elemento React — recebe mouseenter/mouseleave/focus/blur
 *     via wrapper <span style={{display:'inline-block'}}>
 */
export default function DarkTooltip({
  content,
  position = 'top',
  delay = 400,
  maxWidth = 240,
  block = false,
  children,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: -9999, left: -9999, ready: false });
  const [actualPosition, setActualPosition] = useState(position);
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);
  const timerRef = useRef(null);

  const cancelTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const show = () => {
    cancelTimer();
    timerRef.current = setTimeout(() => {
      // Reset estado pra disparar nova medição
      setCoords({ top: -9999, left: -9999, ready: false });
      setActualPosition(position);
      setOpen(true);
    }, delay);
  };

  const hide = () => {
    cancelTimer();
    setOpen(false);
  };

  // Cleanup do timer no unmount
  useEffect(() => () => cancelTimer(), []);

  // PASS 2: mede trigger + tooltip reais e calcula posição final.
  // useLayoutEffect roda SINCRONAMENTE antes do paint do browser,
  // então não há flash visual entre os dois passes.
  useLayoutEffect(() => {
    if (!open) return;
    if (!anchorRef.current || !tooltipRef.current) return;
    const triggerRect = anchorRef.current.getBoundingClientRect();
    if (triggerRect.width === 0 && triggerRect.height === 0) {
      // Defesa: trigger sem dimensões. Não faz sentido posicionar.
      // eslint-disable-next-line no-console
      console.warn('[DarkTooltip] Trigger tem rect 0×0 — wrapper pode estar com display:contents ou filho com display incompatível.');
      return;
    }
    const tipRect = tooltipRef.current.getBoundingClientRect();
    const next = computePosition({
      trigger: triggerRect,
      tooltip: tipRect,
      preferredPosition: position,
      maxWidth,
    });
    setActualPosition(next.position);
    setCoords({ top: next.top, left: next.left, ready: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, position, maxWidth, content]);

  // Recalcula em scroll/resize. NÃO fecha o tooltip — segue o anchor.
  useEffect(() => {
    if (!open) return undefined;
    const recalc = () => {
      if (!anchorRef.current || !tooltipRef.current) return;
      const triggerRect = anchorRef.current.getBoundingClientRect();
      if (triggerRect.width === 0 && triggerRect.height === 0) return;
      const tipRect = tooltipRef.current.getBoundingClientRect();
      const next = computePosition({
        trigger: triggerRect,
        tooltip: tipRect,
        preferredPosition: position,
        maxWidth,
      });
      setActualPosition(next.position);
      setCoords({ top: next.top, left: next.left, ready: true });
    };
    window.addEventListener('scroll', recalc, true);
    window.addEventListener('resize', recalc);
    return () => {
      window.removeEventListener('scroll', recalc, true);
      window.removeEventListener('resize', recalc);
    };
  }, [open, position, maxWidth]);

  if (!content) return children;

  return (
    <>
      <span
        ref={anchorRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        style={
          block
            ? { display: 'block', width: '100%', minWidth: 0 }
            : { display: 'inline-block' }
        }
      >
        {children}
      </span>
      {open && createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{
            position: 'fixed',
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            maxWidth: `${maxWidth}px`,
            padding: '6px 10px',
            background: 'var(--dp-bg-elevated)',
            color: 'var(--dp-text-primary)',
            border: '1px solid rgba(13, 174, 255, 0.15)',
            borderRadius: '6px',
            fontSize: '11px',
            fontFamily: 'var(--dp-font)',
            lineHeight: 1.45,
            boxShadow: '0 4px 16px rgba(0,0,0,0.3), 0 0 0 1px rgba(13,174,255,0.04)',
            zIndex: 100000,
            pointerEvents: 'none',
            opacity: coords.ready ? 1 : 0,
            transition: 'opacity 0.12s ease-out',
            // No primeiro render (ready:false) o tooltip está em (-9999,-9999)
            // com opacity:0 — invisível pro usuário. useLayoutEffect mede e
            // re-posiciona ANTES do paint, então não há flash.
          }}
        >
          {content}
          {/* Arrow */}
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              ...arrowStyles(actualPosition),
              width: '8px',
              height: '8px',
              background: 'var(--dp-bg-elevated)',
              borderRight: '1px solid rgba(13, 174, 255, 0.15)',
              borderBottom: '1px solid rgba(13, 174, 255, 0.15)',
            }}
          />
        </div>,
        document.body,
      )}
    </>
  );
}

/**
 * Calcula coords finais usando dimensões REAIS do tooltip e do trigger.
 * Flip automático (top↔bottom, left↔right) se o lado preferido não couber.
 * Clamp final em viewport pra nunca sair da tela.
 */
function computePosition({ trigger, tooltip, preferredPosition, maxWidth }) {
  const MARGIN = 8;   // margem mínima do viewport
  const GAP = 8;      // distância entre anchor e tooltip
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  // Dimensões REAIS do tooltip já renderizado. Fallback pra estimativa se
  // por algum motivo vier 0 (não deveria).
  const tipW = tooltip.width > 0 ? tooltip.width : Math.min(maxWidth, viewportW - 2 * MARGIN);
  const tipH = tooltip.height > 0 ? tooltip.height : 36;

  // Espaços disponíveis em cada lado do anchor
  const spaceAbove = trigger.top - MARGIN;
  const spaceBelow = viewportH - trigger.bottom - MARGIN;
  const spaceLeft = trigger.left - MARGIN;
  const spaceRight = viewportW - trigger.right - MARGIN;

  // Smart flip: troca lado se não couber e o oposto tiver mais espaço
  let activePos = preferredPosition;
  if (preferredPosition === 'top' && spaceAbove < tipH + GAP && spaceBelow > spaceAbove) {
    activePos = 'bottom';
  } else if (preferredPosition === 'bottom' && spaceBelow < tipH + GAP && spaceAbove > spaceBelow) {
    activePos = 'top';
  } else if (preferredPosition === 'left' && spaceLeft < tipW + GAP && spaceRight > spaceLeft) {
    activePos = 'right';
  } else if (preferredPosition === 'right' && spaceRight < tipW + GAP && spaceLeft > spaceRight) {
    activePos = 'left';
  }

  let top;
  let left;
  switch (activePos) {
    case 'bottom':
      top = trigger.bottom + GAP;
      left = trigger.left + trigger.width / 2 - tipW / 2;
      break;
    case 'left':
      top = trigger.top + trigger.height / 2 - tipH / 2;
      left = trigger.left - tipW - GAP;
      break;
    case 'right':
      top = trigger.top + trigger.height / 2 - tipH / 2;
      left = trigger.right + GAP;
      break;
    case 'top':
    default:
      top = trigger.top - tipH - GAP;
      left = trigger.left + trigger.width / 2 - tipW / 2;
      break;
  }

  // Clamp horizontal
  if (left < MARGIN) left = MARGIN;
  if (left + tipW + MARGIN > viewportW) left = viewportW - tipW - MARGIN;
  // Clamp vertical
  if (top < MARGIN) top = MARGIN;
  if (top + tipH + MARGIN > viewportH) top = Math.max(MARGIN, viewportH - tipH - MARGIN);

  return { top, left, position: activePos };
}

/**
 * CSS overrides pra setinha apontando pro anchor.
 * Mantida igual ao código anterior.
 */
function arrowStyles(position) {
  switch (position) {
    case 'bottom':
      return { top: '-5px', left: '50%', transform: 'translateX(-50%) rotate(225deg)' };
    case 'left':
      return { top: '50%', right: '-5px', transform: 'translateY(-50%) rotate(315deg)' };
    case 'right':
      return { top: '50%', left: '-5px', transform: 'translateY(-50%) rotate(135deg)' };
    case 'top':
    default:
      return { bottom: '-5px', left: '50%', transform: 'translateX(-50%) rotate(45deg)' };
  }
}
